/**
 * 管理员用户列表
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '../../services/adminApi';
import { AdminUserItem } from '../../types';
import Pagination from '../../components/Pagination';

const STATUS_OPTIONS = [
  { value: '', label: '全部' },
  { value: 'active', label: '有效' },
  { value: 'expired', label: '已过期' },
  { value: 'none', label: '从未付费' },
];

const AdminUsers: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<AdminUserItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [subscriptionStatus, setSubscriptionStatus] = useState('');

  const fetchUsers = (p: number) => {
    setLoading(true);
    adminApi
      .getUsers({
        page: p,
        page_size: 20,
        search: search || undefined,
        subscription_status: subscriptionStatus || undefined,
      })
      .then((res) => {
        const data = res.data.data;
        setUsers(data.items);
        setTotal(data.total);
        setTotalPages(Math.ceil(data.total / data.page_size));
      })
      .catch(() => setError('加载用户列表失败'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchUsers(page);
  }, [page, search, subscriptionStatus]);

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const subStatusTag = (expiry: string | null): { text: string; color: string } => {
    if (!expiry) return { text: '从未付费', color: 'bg-accent text-foreground' };
    if (new Date(expiry) > new Date()) return { text: new Date(expiry).toLocaleDateString('zh-CN') + ' 到期', color: 'bg-green-100 text-green-700' };
    return { text: '已过期', color: 'bg-red-100 text-red-600' };
  };

  return (
    <div>
      <h2 className="text-xl font-bold text-foreground mb-6">用户管理</h2>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">{error}</div>
      )}

      <div className="mb-4 flex flex-wrap gap-3">
        <input
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          placeholder="搜索用户名"
          className="px-3 py-2 border border-border rounded-md text-sm w-48"
        />
        <select
          value={subscriptionStatus}
          onChange={(e) => { setSubscriptionStatus(e.target.value); setPage(1); }}
          className="px-3 py-2 border border-border rounded-md text-sm"
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <button
          onClick={handleSearch}
          className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
        >
          搜索
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">加载中...</p>
        </div>
      ) : users.length === 0 ? (
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-400">暂无用户</p>
        </div>
      ) : (
        <>
          <div className="bg-card rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-border text-sm">
              <thead className="bg-background">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">用户名</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">邮箱</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">角色</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">订阅状态</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">项目数</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">注册时间</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {users.map((u) => {
                  const tag = subStatusTag(u.subscription_expiry);
                  return (
                    <tr
                      key={u.id}
                      onClick={() => navigate(`/admin/users/${u.id}`)}
                      className={`cursor-pointer hover:bg-accent ${!u.is_active ? 'opacity-60' : ''}`}
                    >
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="font-medium text-foreground">{u.username}</span>
                        {!u.is_active && <span className="ml-2 text-xs text-red-500">已禁用</span>}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-foreground">{u.email}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {u.role === 'admin' ? (
                          <span className="px-2 py-0.5 text-xs bg-primary/10 text-primary rounded">管理员</span>
                        ) : (
                          <span className="text-muted-foreground">用户</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`px-2 py-0.5 text-xs rounded ${tag.color}`}>{tag.text}</span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-foreground">{u.project_count}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">
                        {new Date(u.created_at).toLocaleDateString('zh-CN')}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pagination page={page} totalPages={totalPages} total={total} pageSize={20} onPageChange={setPage} />
        </>
      )}
    </div>
  );
};

export default AdminUsers;
